import { useAuth0 } from "@auth0/auth0-react";
import { InfiniteData, UseInfiniteQueryResult, UseQueryResult, useInfiniteQuery } from "@tanstack/react-query";
import { FeatureFlags } from "@utils/FeatureFlags/generated-flags";
import { customClient } from "@utils/helpers";
import { format } from "date-fns";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, { PropsWithChildren, createContext, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import {
  CalendarDateRange,
  SearchableDropdownItem,
  SelectItem,
  VideoPlayerHandle,
} from "@fronterahealth/frontera-ui-components";

import {
  GetMediaByCaregiverByMediaIdQuery,
  GetMediaByCaregiverDocument,
  GetMediaByCaregiverQuery,
  GetMediaByProviderByMediaIdQuery,
  GetMediaByProviderDocument,
  GetMediaByProviderQuery,
  GetMediaCommentsByMediaIdQuery,
  GetSessionSummaryBySessionIdQuery,
  GetTranscriptDataByMediaIdDocument,
  MediaCommentFilterEnum,
  MediaSortByEnum,
  TranscriptLineRecordTypeEdge,
  VideoSessionStatusEnums,
  useGetMediaByCaregiverByMediaIdQuery,
  useGetMediaByProviderByMediaIdQuery,
  useGetMediaCommentsByMediaIdQuery,
  useGetSessionSummaryBySessionIdQuery,
} from "@api/graphql/types-and-hooks";
import { Comment, transformCommentData } from "@pages/VideoSessions/components/Comments/helper";
import { MediaCardType } from "@pages/VideoSessions/components/MediaCards";
import { SideMomentPanelItem } from "@pages/VideoSessions/types";
import { useAdminData } from "@providers/AdminDataProvider";

export enum VideoSessionTab {
  Activity = "activity",
  Transcript = "transcript",
  Summary = "summary",
  Moments = "moments",
}

export enum VideoSessionMode {
  /** Reads `mediaId` from the URL — used by the /video-sessions/:mediaId page. */
  Routed = "routed",
  /** Reads `mediaId` from a prop — used by inline mounts (e.g. expert chat). */
  Overlay = "overlay",
}
type CombinedMediaQueryResult = InfiniteData<
  GetMediaByProviderQuery["getMediaByProvider"] | GetMediaByCaregiverQuery["getMediaByCaregiver"],
  unknown
>;
interface VideoProvidersContextI {
  selectedTab: VideoSessionTab;
  setSelectedTab: (val: VideoSessionTab) => void;
  selectedMedia: MediaCardType | null; // This will now be derived from URL
  setSelectedMedia: React.Dispatch<React.SetStateAction<MediaCardType | null>>;
  filterSelectedClients: SearchableDropdownItem[];
  setFilterSelectedClients: (val: SearchableDropdownItem[]) => void;
  filterSelectedProviders: SearchableDropdownItem[];
  setFilterSelectedProviders: (val: SearchableDropdownItem[]) => void;
  filterSelectedCaregivers: SearchableDropdownItem[];
  setFilterSelectedCaregivers: (val: SearchableDropdownItem[]) => void;
  filterSessionDates: CalendarDateRange | undefined;
  setFilterSessionDates: (val: CalendarDateRange) => void;
  filterSelectedRating: SelectItem | undefined;
  setFilterSelectedRating: (val: SelectItem | undefined) => void;
  playerRef: React.RefObject<VideoPlayerHandle | null>;
  currentTime: number;
  setCurrentTime: (time: number) => void;
  sortBy: SelectItem;
  setSortBy: (val: SelectItem) => void;
  totalFilteredMediaCount: number;
  setTotalFilteredMediaCount: (val: number) => void;
  filteredCommentType: SearchableDropdownItem[] | [];
  setFilteredCommentType: (val: SearchableDropdownItem[]) => void;
  commentsByMediaIdQuery: UseQueryResult<GetMediaCommentsByMediaIdQuery, unknown>;
  editingCommentId: string | null;
  setEditingCommentId: (val: string | null) => void;
  allTranscriptData: TranscriptLineRecordTypeEdge[] | [];
  isTranscriptLoading: boolean;
  clearFilters: () => void;
  clearFeedFilters: () => void;
  isVideoDetailsPage: boolean;
  getSessionSummaryBySessionIdQuery: UseQueryResult<GetSessionSummaryBySessionIdQuery, unknown>;

  isMediaLoading: boolean;
  setIsMediaLoading: (val: boolean) => void;
  allFilteredMedias: MediaCardType[];
  setAllFilteredMedias: React.Dispatch<React.SetStateAction<MediaCardType[]>>;
  allFilteredComments: Comment[];
  setAllFilteredComments: React.Dispatch<React.SetStateAction<Comment[]>>;
  filterIsReviewed: boolean | undefined;
  setFilterIsReviewed: (val: boolean | undefined) => void;
  isFilterActive: boolean | undefined;
  setIsFilterActive: (val: boolean) => void;
  filterByUploadStatus: SearchableDropdownItem[];
  setFilterByUploadStatus: (val: SearchableDropdownItem[]) => void;

  sidebarMomentsList: SideMomentPanelItem[];
  setSidebarMomentsList: (val: SideMomentPanelItem[]) => void;

  filteredMomentType: SearchableDropdownItem[] | [];
  setFilteredMomentType: (val: SearchableDropdownItem[]) => void;

  getMediaQuery: UseInfiniteQueryResult<CombinedMediaQueryResult, Error>;

  deletedMediaIds: Set<string>;
  setDeletedMediaIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  viewedMediaMap: Record<string, boolean>;
  setViewedMediaMap: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  otherMaladaptiveBehaviorEnabled: boolean;

  getMediaByIdQuery:
    | UseQueryResult<GetMediaByProviderByMediaIdQuery, unknown>
    | UseQueryResult<GetMediaByCaregiverByMediaIdQuery, unknown>;
}

const VideoSessionContext = createContext<VideoProvidersContextI | undefined>(undefined);

export const useVideoSessionData = () => {
  const context = useContext(VideoSessionContext);
  if (context === undefined) {
    throw new Error("useVideoSessionData must be used within a VideoSessionProvider");
  }
  return context;
};
export const defaultSortBy = {
  id: MediaSortByEnum.DescSessionUploadDate,
  primary: "Newest Upload",
};
const momentsList: SideMomentPanelItem[] = [
  { label: "Aggression", isVisible: true, color: "globemallow-500" },
  { label: "Screaming & Crying", isVisible: true, color: "hatch-500" },
];
interface VideoSessionProviderProps {
  /** See {@link VideoSessionMode}. Defaults to Routed. */
  mode?: VideoSessionMode;
  mediaIdOverride?: string;
}

export const VideoSessionProvider: React.FC<PropsWithChildren<VideoSessionProviderProps>> = ({
  children,
  mode = VideoSessionMode.Routed,
  mediaIdOverride,
}) => {
  const { mediaId: urlMediaId } = useParams();
  const { pathname } = useLocation();
  const mediaId = mode === VideoSessionMode.Overlay ? mediaIdOverride : urlMediaId;
  const isOverlayMode = mode === VideoSessionMode.Overlay;

  const { isUserCaregiver } = useAdminData();
  const { getAccessTokenSilently } = useAuth0();
  const flags = useFlags<FeatureFlags>();
  const otherMaladaptiveBehaviorEnabled = flags["other-maladaptive-ui-presentation"];

  // Fetch media data based on URL parameter
  const getMediaByIdQuery = isUserCaregiver
    ? useGetMediaByCaregiverByMediaIdQuery(
        { mediaId: mediaId || "" },
        { enabled: Boolean(mediaId), refetchOnWindowFocus: false },
      )
    : useGetMediaByProviderByMediaIdQuery(
        { mediaId: mediaId || "" },
        { enabled: Boolean(mediaId), refetchOnWindowFocus: false },
      );

  const { data: mediaData } = getMediaByIdQuery;
  const [selectedMedia, setSelectedMedia] = useState<MediaCardType | null>(null);
  const [selectedTab, setSelectedTab] = useState<VideoSessionTab>(VideoSessionTab.Activity);
  const [filterSelectedClients, setFilterSelectedClients] = useState<SearchableDropdownItem[]>([]);
  const [filterSelectedProviders, setFilterSelectedProviders] = useState<SearchableDropdownItem[]>([]);
  const [filterSelectedCaregivers, setFilterSelectedCaregivers] = useState<SearchableDropdownItem[]>([]);

  const [filterSessionDates, setFilterSessionDates] = useState<CalendarDateRange | undefined>({});
  const [filterSelectedRating, setFilterSelectedRating] = useState<SelectItem | undefined>();
  const [filterIsReviewed, setFilterIsReviewed] = useState<boolean | undefined>();
  const [isFilterActive, setIsFilterActive] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState<SelectItem>(defaultSortBy);
  const [filterByUploadStatus, setFilterByUploadStatus] = useState<SearchableDropdownItem[]>([]);

  const [totalFilteredMediaCount, setTotalFilteredMediaCount] = useState<number>(0);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const playerRef = useRef<VideoPlayerHandle>(null);

  const [filteredCommentType, setFilteredCommentType] = useState<SearchableDropdownItem[]>([]);
  const [editingCommentId, setEditingCommentId] = useState<string | null>("");
  const [allTranscriptData, setAllTranscriptData] = useState<TranscriptLineRecordTypeEdge[] | []>([]);
  const [isTranscriptLoading, setIsTranscriptLoading] = useState<boolean>(false);
  const [sidebarMomentsList, setSidebarMomentsList] = useState<SideMomentPanelItem[]>(momentsList);

  const [filteredMomentType, setFilteredMomentType] = useState<SearchableDropdownItem[]>([]);
  const [deletedMediaIds, setDeletedMediaIds] = useState<Set<string>>(new Set());
  const [viewedMediaMap, setViewedMediaMap] = useState<Record<string, boolean>>({});

  const queryKey = React.useMemo(
    () => ["getMediaComments", mediaId, filteredCommentType],
    [mediaId, filteredCommentType],
  );

  const [isMediaLoading, setIsMediaLoading] = useState<boolean>(false);
  const [allFilteredMedias, setAllFilteredMedias] = useState<MediaCardType[]>([]);
  const [allFilteredComments, setAllFilteredComments] = useState<Comment[]>([]);

  const commentsByMediaIdQuery = useGetMediaCommentsByMediaIdQuery(
    {
      mediaId: mediaId || "",
      commentTypes: filteredCommentType.length
        ? (filteredCommentType.map((item) => item?.id) as unknown as MediaCommentFilterEnum)
        : undefined,
    },
    {
      queryKey,
      enabled: Boolean(mediaId) && (isOverlayMode || pathname.includes(`/video-sessions/${mediaId}`)),
    },
  );

  const { data: commentsData } = commentsByMediaIdQuery;

  useEffect(() => {
    if (commentsData) {
      const comments: Comment[] = transformCommentData(commentsData);
      setAllFilteredComments(comments);
    }
  }, [commentsData]);

  const transcriptByMediaIdQuery = useInfiniteQuery({
    queryKey: ["getTranscriptDataByMediaId", mediaId],
    queryFn: async ({ pageParam = { after: null } }) => {
      const { after } = pageParam;
      const variables = { mediaId, after };
      const token = await getAccessTokenSilently();
      const response = await customClient({ token, document: GetTranscriptDataByMediaIdDocument, variables });

      return response.getTranscriptDataByMediaId;
    },
    initialPageParam: { after: null },
    getNextPageParam: (lastPage) => {
      if (lastPage.pageInfo.hasNextPage) {
        return {
          after: lastPage.pageInfo.endCursor, // Use the cursor for more efficient pagination
        };
      }
      return undefined; // No more pages to fetch
    },
    // In overlay mode (currently chat-only) we don't render the
    // transcript tab, so skip the network round-trip entirely.
    enabled: Boolean(mediaId) && !isOverlayMode,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const {
    data: transcriptData,
    isFetchingNextPage: isTranscriptNextPageFetching,
    isFetching: isTranscriptFetching,
    hasNextPage: isTranscriptHasNextPage,
    fetchNextPage: fetchTranscriptNextPage,
  } = transcriptByMediaIdQuery;

  // Transform mediaData into MediaCardType format

  useEffect(() => {
    if (!mediaData) {
      setSelectedMedia(null);
      return;
    }

    let media;

    if (isUserCaregiver && "getMediaByCaregiverByMediaId" in mediaData) {
      media = mediaData.getMediaByCaregiverByMediaId;
    } else if ("getMediaByProviderByMediaId" in mediaData) {
      media = mediaData.getMediaByProviderByMediaId;
    } else {
      setSelectedMedia(null);
      return;
    }

    if (!media) {
      setSelectedMedia(null);
      return;
    }

    setSelectedMedia({
      id: media.id,
      createdDate: media.createdDate,
      mediaType: media.mediaType,
      mediaStatus: media.mediaStatus,
      videoSession: media.videoSession,
      commentsCount: media.commentsCount,
      mediaClassifications: media.mediaClassifications,
      signedUploadUrl: media.signedUploadUrl,
      // Chat-uploaded media has no VideoSession, so signedUploadUrl is
      // null for it. mediaAttachmentUrl is resolved unconditionally and
      // is what the overlay falls back to in chat mode.
      mediaAttachmentUrl: media.mediaAttachmentUrl,
      isViewed: media.isViewed,
    } as MediaCardType);
  }, [mediaData, isUserCaregiver]);

  const getSessionSummaryBySessionIdQuery = useGetSessionSummaryBySessionIdQuery(
    {
      videoSessionId: selectedMedia?.videoSession?.id || "",
    },
    {
      queryKey: ["getSessionSummaryBySessionId", selectedMedia],
      enabled: Boolean(selectedMedia?.videoSession?.id),
      refetchOnWindowFocus: false,
      retry: false,
    },
  );

  const fetchMedia = async ({ pageParam = { after: null } }) => {
    const { after } = pageParam;

    const variables = {
      after,
      sortBy: (sortBy.id as unknown as MediaSortByEnum) || MediaSortByEnum.DescSessionUploadDate,
      learnerIds: filterSelectedClients.map((item) => item.id),
      providerIds: !isUserCaregiver ? filterSelectedProviders.map((item) => item.id) : undefined,
      caregiverIds: !isUserCaregiver ? filterSelectedCaregivers.map((item) => item.id) : undefined,
      sessionStartDate: filterSessionDates?.startDate ? format(filterSessionDates.startDate, "yyyy-MM-dd") : undefined,
      sessionEndDate: filterSessionDates?.endDate ? format(filterSessionDates.endDate, "yyyy-MM-dd") : undefined,
      sessionRating: !isUserCaregiver ? parseInt(filterSelectedRating?.id || "") : undefined,
      isReviewed: filterIsReviewed,
      sessionStatus: finalUploadStatus.length ? (finalUploadStatus as unknown as VideoSessionStatusEnums[]) : undefined,
    };

    const token = await getAccessTokenSilently();

    const response = await customClient({
      token,
      document: isUserCaregiver ? GetMediaByCaregiverDocument : GetMediaByProviderDocument,
      variables,
    });

    return isUserCaregiver ? response.getMediaByCaregiver : response.getMediaByProvider;
  };

  const finalUploadStatus = filterByUploadStatus.flatMap((item) => {
    try {
      const parsed = JSON.parse(item.id);
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      return [item.id];
    }
  });

  const getMediaQuery = useInfiniteQuery({
    queryKey: [
      isUserCaregiver ? "getMediaByCaregiver" : "getMediaByProvider",
      filterSelectedClients,
      !isUserCaregiver ? filterSelectedCaregivers : null,
      !isUserCaregiver ? filterSelectedProviders : null,
      filterSessionDates?.startDate,
      filterSessionDates?.endDate,
      !isUserCaregiver ? filterSelectedRating : null,
      filterIsReviewed,
      finalUploadStatus,
      sortBy,
    ],
    queryFn: fetchMedia,
    initialPageParam: { after: null },
    getNextPageParam: (lastPage) => {
      return lastPage.pageInfo.hasNextPage ? { after: lastPage.pageInfo.endCursor } : undefined;
    },
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  useEffect(() => {
    const filteredList = otherMaladaptiveBehaviorEnabled
      ? momentsList
      : momentsList.filter((item) => item.label !== "Aggression");

    setSidebarMomentsList(filteredList);
  }, [otherMaladaptiveBehaviorEnabled, momentsList]);

  useEffect(() => {
    setIsFilterActive(
      filterSelectedClients.length > 0 ||
        filterSelectedProviders.length > 0 ||
        filterSelectedCaregivers.length > 0 ||
        filterByUploadStatus.length > 0 ||
        (filterSessionDates?.startDate !== undefined && filterSessionDates?.endDate !== undefined) ||
        filterSelectedRating !== undefined,
    );
  }, [
    filterSelectedClients,
    filterSelectedCaregivers,
    filterSelectedProviders,
    filterSessionDates,
    filterSelectedRating,
    filterByUploadStatus,
  ]);

  useEffect(() => {
    if (isTranscriptFetching || isTranscriptNextPageFetching) {
      setIsTranscriptLoading(true);
    } else {
      setIsTranscriptLoading(false);
    }
  }, [isTranscriptFetching, isTranscriptNextPageFetching]);

  useEffect(() => {
    if (isTranscriptHasNextPage && !isTranscriptNextPageFetching) {
      fetchTranscriptNextPage();
    }
  }, [isTranscriptHasNextPage, isTranscriptNextPageFetching, fetchTranscriptNextPage]);

  useEffect(() => {
    if (transcriptData && !isTranscriptHasNextPage && !isTranscriptNextPageFetching) {
      const dataTemp = transcriptData.pages
        .map((group) => {
          return [...group.edges];
        })
        .flat(); // Append new edges
      setAllTranscriptData(dataTemp);
    }
  }, [transcriptData, isTranscriptHasNextPage, isTranscriptNextPageFetching]);

  const clearFilters = () => {
    setSelectedTab(VideoSessionTab.Activity);
    setFilteredCommentType([]);
  };

  const clearFeedFilters = () => {
    setSortBy(defaultSortBy);
    setFilterSelectedClients([]);
    setFilterSelectedProviders([]);
    setFilterSessionDates({});
    setFilterByUploadStatus([]);
    setFilterSelectedRating(undefined);
    setIsFilterActive(false);
    setFilterSelectedCaregivers([]);
  };

  return (
    <VideoSessionContext.Provider
      value={{
        selectedTab,
        setSelectedTab,
        selectedMedia,
        setSelectedMedia,
        filterSelectedClients,
        setFilterSelectedClients,
        filterSelectedProviders,
        setFilterSelectedProviders,
        filterSelectedCaregivers,
        setFilterSelectedCaregivers,
        filterSessionDates,
        setFilterSessionDates,
        filterSelectedRating,
        setFilterSelectedRating,
        sortBy,
        setSortBy,
        totalFilteredMediaCount,
        setTotalFilteredMediaCount,
        playerRef,
        currentTime,
        setCurrentTime,
        filteredCommentType,
        setFilteredCommentType,
        commentsByMediaIdQuery,
        editingCommentId,
        setEditingCommentId,

        allTranscriptData,
        isTranscriptLoading,
        clearFilters,
        isVideoDetailsPage: isOverlayMode || !!urlMediaId,
        getSessionSummaryBySessionIdQuery,
        isMediaLoading,
        setIsMediaLoading,
        allFilteredMedias,
        setAllFilteredMedias,
        allFilteredComments,
        setAllFilteredComments,
        filterIsReviewed,
        setFilterIsReviewed,
        clearFeedFilters,
        isFilterActive,
        setIsFilterActive,
        filterByUploadStatus,
        setFilterByUploadStatus,

        sidebarMomentsList,
        setSidebarMomentsList,

        filteredMomentType,
        setFilteredMomentType,

        getMediaQuery,
        deletedMediaIds,
        setDeletedMediaIds,
        viewedMediaMap,
        setViewedMediaMap,
        otherMaladaptiveBehaviorEnabled,
        getMediaByIdQuery,
      }}
    >
      {children}
    </VideoSessionContext.Provider>
  );
};
